import { useEffect, useMemo, useState } from 'react';
import {
  useAtRisk,
  useKpis,
  useMe,
  useUsageByApp,
  useUsageByBusinessLine,
  useUsageTrend,
  useUsers,
} from '@/lib/queries';
import { AppUsageBars } from '@/components/AppUsageBars';
import { AppUsersDrawer } from '@/components/AppUsersDrawer';
import { BusinessLineTable } from '@/components/BusinessLineTable';
import { KpiCard } from '@/components/KpiCard';
import { RiskList } from '@/components/RiskList';
import { TrendChart } from '@/components/TrendChart';
import { UserDetailDrawer } from '@/components/UserDetailDrawer';
import { UserMenu } from '@/components/UserMenu';
import type { FiltersState, PeriodDays, SortState } from '@/components/UsersTable';
import { UsersTable } from '@/components/UsersTable';

interface DashboardProps {
  onNavigate?: (view: 'dashboard' | 'config') => void;
}

const PAGE_SIZE = 25;

function pct(n: number, d: number): string {
  if (!d) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

export function Dashboard({ onNavigate }: DashboardProps = {}) {
  const me = useMe();
  const lines = useMemo(() => me.data?.business_lines ?? [], [me.data]);
  const isAdmin = me.data?.is_app_admin ?? false;

  // Selección inicial: si solo tiene una filial, fija; si admin, "Todas"; si manager con varias, la primera.
  const [businessLine, setBusinessLine] = useState<string | null>(null);
  useEffect(() => {
    if (businessLine !== null || lines.length === 0) return;
    if (isAdmin) {
      setBusinessLine('__all__');
    } else if (lines.length === 1) {
      setBusinessLine(lines[0] ?? null);
    } else {
      setBusinessLine(lines[0] ?? null);
    }
  }, [isAdmin, lines, businessLine]);

  const isAggregate = businessLine === '__all__';
  const scoped = isAggregate ? null : businessLine;

  const kpis = useKpis();
  const byApp = useUsageByApp(scoped);
  const byBL = useUsageByBusinessLine();
  const risk = useAtRisk(scoped, 10);

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FiltersState>({
    q: '',
    department: null,
    hasLicense: null,
    onlyInactive: false,
  });
  const [selectedUpn, setSelectedUpn] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<{ key: string; label: string } | null>(
    null,
  );
  const [sort, setSort] = useState<SortState>({ field: 'full_name', dir: 'asc' });
  const [trendDays, setTrendDays] = useState(30);
  const [periodDays, setPeriodDays] = useState<PeriodDays>(30);
  const trend = useUsageTrend(scoped, trendDays);
  const users = useUsers({
    page,
    pageSize: PAGE_SIZE,
    businessLine: scoped,
    onlyInactive: filters.onlyInactive,
    q: filters.q,
    department: filters.department,
    hasLicense: filters.hasLicense,
    sortBy: sort.field,
    sortDir: sort.dir,
    periodDays,
  });

  return (
    <main className="mx-auto max-w-7xl px-6 py-10">
      <header className="mb-10 flex items-center justify-between border-b border-ink-800 pb-6">
        <div className="flex items-baseline gap-6">
          <div>
            <div className="label">Copilot Usage</div>
            <h1 className="mt-1 text-2xl font-light tracking-tight text-ink-50">
              {isAggregate
                ? 'Adopción · todas las filiales'
                : (scoped ?? '—')}
            </h1>
          </div>
          {lines.length > 1 || isAdmin ? (
            <select
              value={businessLine ?? ''}
              onChange={(e) => {
                setBusinessLine(e.target.value);
                setPage(1);
                setFilters({
                  q: '',
                  department: null,
                  hasLicense: null,
                  onlyInactive: false,
                });
              }}
              className="self-center border border-ink-800 bg-ink-900 px-3 py-1.5 text-sm text-ink-100 focus:border-ink-500"
            >
              {isAdmin ? <option value="__all__">Todas las filiales</option> : null}
              {lines.map((bl) => (
                <option key={bl} value={bl}>
                  {bl}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <UserMenu me={me.data} onNavigate={onNavigate} />
      </header>

      {isAggregate ? (
        <AdminOverview
          kpis={kpis}
          byBL={byBL}
          byApp={byApp}
          risk={risk}
          onPick={(bl) => {
            setBusinessLine(bl);
            setPage(1);
            setFilters({
              q: '',
              department: null,
              hasLicense: null,
              onlyInactive: false,
            });
          }}
        />
      ) : (
        <ManagerView
          kpis={kpis}
          byApp={byApp}
          users={users}
          risk={risk}
          trend={trend}
          trendDays={trendDays}
          onTrendDaysChange={setTrendDays}
          page={page}
          onPageChange={setPage}
          filters={filters}
          onFiltersChange={(f) => {
            setFilters(f);
            setPage(1);
          }}
          onSelectUser={setSelectedUpn}
          sort={sort}
          onSortChange={(s) => {
            setSort(s);
            setPage(1);
          }}
          businessLine={scoped}
          period={periodDays}
          onPeriodChange={(p) => {
            setPeriodDays(p);
            setPage(1);
          }}
        />
      )}
      <AppUsersDrawer
        app={selectedApp}
        businessLine={scoped}
        onClose={() => setSelectedApp(null)}
        onSelectUser={(upn) => {
          setSelectedApp(null);
          setSelectedUpn(upn);
        }}
      />
      <UserDetailDrawer upn={selectedUpn} onClose={() => setSelectedUpn(null)} />
    </main>
  );

  function AdminOverview(props: {
    kpis: ReturnType<typeof useKpis>;
    byBL: ReturnType<typeof useUsageByBusinessLine>;
    byApp: ReturnType<typeof useUsageByApp>;
    risk: ReturnType<typeof useAtRisk>;
    onPick: (bl: string) => void;
  }) {
    const k = props.kpis.data;
    return (
      <>
        <section className="grid grid-cols-2 gap-px bg-ink-800 lg:grid-cols-4">
          <KpiCard
            label="Licencias asignadas"
            value={k?.licenses_assigned ?? 0}
            hint={k ? `${pct(k.licenses_assigned, k.users_in_scope)} del total` : undefined}
            loading={props.kpis.isLoading}
          />
          <KpiCard
            label="Adopción 30d"
            value={k ? pct(k.active_users_30d, k.licenses_assigned) : '—'}
            hint={`${k?.active_users_30d ?? 0} usuarios activos`}
            loading={props.kpis.isLoading}
          />
          <KpiCard
            label="Licencia sin uso"
            value={k?.inactive_with_license_30d ?? 0}
            emphasis="warn"
            hint="Candidatos a revocar"
            loading={props.kpis.isLoading}
          />
          <KpiCard
            label="Empleados"
            value={k?.users_in_scope ?? 0}
            loading={props.kpis.isLoading}
          />
        </section>

        <div className="mt-10">
          <BusinessLineTable
            data={props.byBL.data}
            loading={props.byBL.isLoading}
            selected={null}
            onSelect={(bl) => {
              if (bl) props.onPick(bl);
            }}
          />
        </div>

        <div className="mt-8">
          <AppUsageBars
            data={props.byApp.data}
            loading={props.byApp.isLoading || props.byApp.isFetching}
            onSelectApp={setSelectedApp}
          />
        </div>

        <div className="mt-8">
          <RiskList
            data={props.risk.data}
            loading={props.risk.isLoading || props.risk.isFetching}
          />
        </div>
      </>
    );
  }

  function ManagerView(props: {
    kpis: ReturnType<typeof useKpis>;
    byApp: ReturnType<typeof useUsageByApp>;
    users: ReturnType<typeof useUsers>;
    risk: ReturnType<typeof useAtRisk>;
    trend: ReturnType<typeof useUsageTrend>;
    trendDays: number;
    onTrendDaysChange: (d: number) => void;
    page: number;
    onPageChange: (p: number) => void;
    filters: FiltersState;
    onFiltersChange: (f: FiltersState) => void;
    onSelectUser: (upn: string) => void;
    sort: SortState;
    onSortChange: (s: SortState) => void;
    businessLine: string | null;
    period: PeriodDays;
    onPeriodChange: (p: PeriodDays) => void;
  }) {
    // KPIs específicos para la filial: derivados del byApp + users (más fiables que /kpis global)
    const employees = props.users.data?.total ?? 0;
    const licenses = props.byApp.data?.licenses_assigned ?? 0;
    // Adopción = max(active por cualquier app) — usamos byApp porque incluye desglose
    const activeAny = props.byApp.data
      ? Math.max(0, ...props.byApp.data.items.map((i) => i.active_users))
      : 0;
    const inactive = Math.max(0, licenses - activeAny);

    return (
      <>
        <section className="grid grid-cols-2 gap-px bg-ink-800 lg:grid-cols-4">
          <KpiCard
            label="Empleados"
            value={employees}
            loading={props.users.isLoading && employees === 0}
          />
          <KpiCard
            label="Licencias"
            value={licenses}
            hint={employees ? `${pct(licenses, employees)} del equipo` : undefined}
            loading={props.byApp.isLoading}
          />
          <KpiCard
            label="Adopción 30d"
            value={pct(activeAny, licenses)}
            hint={`${activeAny} activos`}
            loading={props.byApp.isLoading}
          />
          <KpiCard
            label="Licencia sin uso"
            value={inactive}
            emphasis="warn"
            hint="Candidatos a revocar"
            loading={props.byApp.isLoading}
          />
        </section>

        <div className="mt-10">
          <TrendChart
            data={props.trend.data}
            loading={props.trend.isLoading || props.trend.isFetching}
            days={props.trendDays}
            onDaysChange={props.onTrendDaysChange}
          />
        </div>

        <div className="mt-8">
          <AppUsageBars
            data={props.byApp.data}
            loading={props.byApp.isLoading || props.byApp.isFetching}
            onSelectApp={setSelectedApp}
          />
        </div>

        <div className="mt-8">
          <UsersTable
            data={props.users.data}
            loading={props.users.isLoading || props.users.isFetching}
            page={props.page}
            pageSize={PAGE_SIZE}
            onPageChange={props.onPageChange}
            filters={props.filters}
            onFiltersChange={props.onFiltersChange}
            onSelectUser={props.onSelectUser}
            sort={props.sort}
            onSortChange={props.onSortChange}
            businessLine={props.businessLine}
            period={props.period}
            onPeriodChange={props.onPeriodChange}
          />
        </div>

        <div className="mt-8">
          <RiskList
            data={props.risk.data}
            loading={props.risk.isLoading || props.risk.isFetching}
          />
        </div>
      </>
    );
  }
}
